import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "../styles/tokens.css";
import "../styles/app.css";
import "../styles/chart.css";
import { createBrowserRouter, RouterProvider } from "react-router";
import { installLocaleMessages } from "../shell/i18n";
import { LocaleChoiceProvider } from "../shell/i18n/choice";
import type { Locale } from "../shell/i18n/locales";
import { appLocale } from "../shell/i18n/resolve";
import { App } from "./app";
import { productMessages } from "./product-messages";

async function loadLocale(locale: Locale): Promise<void> {
  installLocaleMessages(locale, await productMessages(locale));
}

/**
 * A DATA ROUTER with one catch-all route, and the screens still declared inside `App`.
 *
 * The BFF serves this shell for any path that is not the API's, so the router owns real
 * addresses rather than fragments — that has not changed. What changed is `createBrowserRouter`
 * in place of `<BrowserRouter>`: `useBlocker` is the only supported way to hold an in-app
 * navigation, it needs a data router, and without it the unsaved-work question could only
 * be asked on the three exits a pane owns. The alternative — intercepting clicks on
 * anchors — catches links and misses every programmatic `navigate()`.
 *
 * One route rather than the whole table lifted up here: `App` renders the shell around
 * its own `<Routes>`, and moving that would mean re-deciding where the rail, the palette
 * and the session live to buy nothing this needs.
 */
const router = createBrowserRouter([{ path: "*", element: <App /> }]);

const root = document.getElementById("root");
if (root) {
  const selected = appLocale();
  void loadLocale(selected)
    .then(() => {
      createRoot(root).render(
        <StrictMode>
          {/* The first locale is installed before React renders one word. A later choice
              loads its catalogue while this complete tree stays in the current language,
              then changes context atomically — no half-translated frame and no reload that
              discards an open form. */}
          <LocaleChoiceProvider initialLocale={selected} loadLocale={loadLocale}>
            <RouterProvider router={router} />
          </LocaleChoiceProvider>
        </StrictMode>,
      );
    })
    .catch((error: unknown) => {
      console.error("riksu: the initial language could not be loaded", error);
      createRoot(root).render(
        <p role="alert">Riksu could not start. Reload the page to try again.</p>,
      );
    });
}
